import type React from "react"
import { useState } from "react"
import {
	Trash2,
	GripVertical,
	Settings,
	CreditCard,
	Grid3x3,
	Table,
	List,
	Plus,
	Eye,
	Layout,
	Code,
	Save,
	Download,
	Maximize2,
	Minimize2,
	ArrowUp,
	ArrowDown,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { SchemaPropertySelector } from "@/components/schema-property-selector"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { FormPreview } from "@/components/form-preview"
import { MonacoEditor } from "@/components/monaco-editor"
import { Checkbox } from "@/components/ui/checkbox"
import { getCSRFToken } from "@/lib/utils"
import { useVisualizer } from "@/lib/visualizer-context"
import { usePage } from "@inertiajs/react"



interface VisualizerCanvasProps {
	config: any
	dataSchema: any
	data: any
	document_classifier_id: number
	onConfigChange: (config: any) => void
	onSchemaChange: (schema: any) => void
	onElementSelect?: (element: any) => void
	selectedElement?: any
}

function LayoutItemRenderer({
	item,
	path,
	tabIndex,
	selectedPath,
	onSelect,
	onDelete,
	onDrop,
	onUpdate,
	onDragElement,
	onMoveElement,
	dataSchema,
	onOpenPropertySelector,
	onDragEnd,
}: {
	item: any
	path: number[]
	tabIndex: number
	selectedPath: number[] | null
	onSelect: (path: number[], tabIndex: number) => void
	onDelete: (path: number[], tabIndex: number) => void
	onDrop: (componentData: string, targetPath: number[], tabIndex: number, insertIndex?: number) => void
	onUpdate: (path: number[], tabIndex: number, updatedItem: any) => void
	onDragElement: (path: number[], tabIndex: number) => void
	onMoveElement: (sourcePath: number[], targetPath: number[], tabIndex: number, insertIndex?: number) => void
	dataSchema: any
	onOpenPropertySelector: (path: number[], tabIndex: number, currentScope?: string) => void
	onDragEnd?: () => void
}) {
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [isDraggingOver, setIsDraggingOver] = useState(false)
	const [keySelectorOpen, setKeySelectorOpen] = useState(false)
	const [draggedKeyPath, setDraggedKeyPath] = useState<[number, number] | null>(null)
	const [isDragging, setIsDragging] = useState(false)
	const [isEditingHeader, setIsEditingHeader] = useState(false)
	const [headerValue, setHeaderValue] = useState(item.header || "")
	const [selectedKeys, setSelectedKeys] = useState<number[]>([])

	const isSelected = selectedPath && JSON.stringify(selectedPath) === JSON.stringify(path)
	const canHaveChildren = item.type === "card" || item.type === "card_array"
	const children = item.children || []
	const hasKeys = (item.type === "card" || item.type === "card_array") && item.keys

	const getIconForType = (type: string) => {
		switch (type) {
			case "card":
				return <CreditCard className="h-4 w-4" />
			case "card_array":
				return <Grid3x3 className="h-4 w-4" />
			case "table":
				return <Table className="h-4 w-4" />
			case "list":
				return <List className="h-4 w-4" />
			default:
				return <CreditCard className="h-4 w-4" />
		}
	}

	const getDisplayLabel = (item: any) => {
		return item.header || item.type
	}

	const handleDragStart = (e: React.DragEvent) => {
		e.stopPropagation()
		e.dataTransfer.setData("elementPath", JSON.stringify({ path, tabIndex }))
		e.dataTransfer.effectAllowed = "move"

		setIsDragging(true)
		onDragElement(path, tabIndex)
	}

	const handleDragEnd = (e: React.DragEvent) => {

		setIsDragging(false)
		onDragEnd?.()
	}

	const handleDrop = (e: React.DragEvent, insertIndex?: number) => {
		e.preventDefault()
		e.stopPropagation()
		setDragOverIndex(null)
		setIsDraggingOver(false)

		const componentData = e.dataTransfer.getData("component")
		const elementPath = e.dataTransfer.getData("elementPath")

		console.log(
			"[v0] Drop detected - componentData:",
			!!componentData,
			"elementPath:",
			!!elementPath,
			"insertIndex:",
			insertIndex,
		)

		if (componentData) {
			console.log(
				"[v0] Dropping new component:",
				JSON.parse(componentData).type,
				"at path:",
				path,
				"insertIndex:",
				insertIndex,
			)
			onDrop(componentData, path, tabIndex, insertIndex)
		} else if (elementPath) {
			const { path: sourcePath, tabIndex: sourceTabIndex } = JSON.parse(elementPath)

			if (sourceTabIndex === tabIndex) {
				onMoveElement(sourcePath, path, tabIndex, insertIndex)
			}
		}
	}

	const handleDragOver = (e: React.DragEvent, insertIndex?: number) => {
		e.preventDefault()
		e.stopPropagation()
		const hasComponent = e.dataTransfer.types.includes("component")
		const hasElementPath = e.dataTransfer.types.includes("elementpath")
		e.dataTransfer.dropEffect = hasElementPath ? "move" : "copy"
		setDragOverIndex(insertIndex ?? (item.children?.length || 0))
		setIsDraggingOver(insertIndex === undefined)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		e.stopPropagation()
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
		const x = e.clientX
		const y = e.clientY
		if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
			setDragOverIndex(null)
			setIsDraggingOver(false)
		}
	}

	const handleKeyDragStart = (e: React.DragEvent, rowIndex: number, colIndex: number) => {
		e.stopPropagation()
		e.dataTransfer.setData("keyPath", JSON.stringify({ path, tabIndex, rowIndex, colIndex }))
		e.dataTransfer.effectAllowed = "move"
		setDraggedKeyPath([rowIndex, colIndex])
	}

	const handleKeyDrop = (e: React.DragEvent, targetRowIndex: number, targetColIndex: number) => {
		e.preventDefault()
		e.stopPropagation()
		setDraggedKeyPath(null)

		const keyPathData = e.dataTransfer.getData("keyPath")
		if (!keyPathData) return

		const { path: sourcePath, tabIndex: sourceTabIndex, rowIndex, colIndex } = JSON.parse(keyPathData)
		if (JSON.stringify(sourcePath) !== JSON.stringify(path) || sourceTabIndex !== tabIndex) return

		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))

		// If the item being moved is part of a row, extract it
		let movedKeyOrRow: string | string[]
		if (Array.isArray(keys[rowIndex])) {
			// If the source is a row, we need to move one of its elements
			if (!Array.isArray(keys[targetRowIndex])) {
				// Cannot drop an element from a row into a non-row position directly, handle as an error or specific logic
				return
			}
			const sourceRow = keys[rowIndex]
			const [movedKey] = sourceRow.splice(colIndex, 1)
			if (sourceRow.length === 0) {
				keys.splice(rowIndex, 1)
			}

			const targetRow = keys[targetRowIndex]
			targetRow.splice(targetColIndex, 0, movedKey)
		} else {
			// Moving a single key
			const [movedKey] = keys.splice(rowIndex, 1)
			keys.splice(targetRowIndex, 0, movedKey)
		}

		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	const handleKeyDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
	}

	const handleAddKey = () => {
		setKeySelectorOpen(true)
	}

	const handleKeySelect = (scope: string) => {
		const keyName = scope.split("/").pop() || "new_key"

		const newItem = { ...item }
		if (!newItem.keys) {
			newItem.keys = []
		}
		// Add as a single string (not in an array)
		newItem.keys.push(keyName)
		onUpdate(path, tabIndex, newItem)
		setKeySelectorOpen(false)
	}

	const handleRemoveKey = (keyIndex: number) => {
		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))
		keys.splice(keyIndex, 1)
		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	const handleGroupKeys = () => {
		if (selectedKeys.length < 2) return

		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))

		// Extract the keys to group
		const keysToGroup = selectedKeys.flatMap((i) => {
			const key = keys[i]
			return Array.isArray(key) ? key : [key]
		})

		// Remove the original keys (in reverse order to maintain indices)
		selectedKeys.sort((a, b) => b - a).forEach((i) => keys.splice(i, 1))

		// Add the grouped keys as an array
		keys.push(keysToGroup)

		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
		setSelectedKeys([])
	}

	const handleUngroupRow = (keyIndex: number) => {
		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))

		const row = keys[keyIndex]
		if (Array.isArray(row)) {
			// Remove the row
			keys.splice(keyIndex, 1)
			// Add each key individually
			row.forEach((key: string) => keys.push(key))
		}

		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	const handleKeyChange = (rowIndex: number, colIndex: number, newValue: string) => {
		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))
		keys[rowIndex][colIndex] = newValue
		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	const handleHeaderChange = (newHeader: string) => {
		const newItem = { ...item, header: newHeader }
		onUpdate(path, tabIndex, newItem)
	}

	const handleHeaderBlur = () => {
		setIsEditingHeader(false)
		if (headerValue !== item.header) {
			handleHeaderChange(headerValue)
		}
	}

	const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault()
			handleHeaderBlur()
		} else if (e.key === "Escape") {
			setHeaderValue(item.header || "")
			setIsEditingHeader(false)
		}
	}

	// Added handler for toggling key selection
	const handleToggleKeySelection = (keyIndex: number) => {
		setSelectedKeys((prev) => {
			if (prev.includes(keyIndex)) {
				return prev.filter((i) => i !== keyIndex)
			} else {
				return [...prev, keyIndex]
			}
		})
	}

	const handleMoveKeyUp = (keyIndex: number) => {
		if (keyIndex === 0) return // Can't move first key up

		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))

			// Swap with previous key
			;[keys[keyIndex - 1], keys[keyIndex]] = [keys[keyIndex], keys[keyIndex - 1]]

		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	const handleMoveKeyDown = (keyIndex: number) => {
		if (keyIndex === item.keys.length - 1) return // Can't move last key down

		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))

			// Swap with next key
			;[keys[keyIndex], keys[keyIndex + 1]] = [keys[keyIndex + 1], keys[keyIndex]]

		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	const handleMoveKeyInRow = (keyIndex: number, colIndex: number, direction: "up" | "down") => {
		const newItem = { ...item }
		const keys = JSON.parse(JSON.stringify(newItem.keys))
		const row = keys[keyIndex]

		if (!Array.isArray(row)) return

		if (direction === "up" && colIndex === 0) return // Can't move first key up
		if (direction === "down" && colIndex === row.length - 1) return // Can't move last key down

		const targetIndex = direction === "up" ? colIndex - 1 : colIndex + 1

			// Swap keys within the row
			;[row[colIndex], row[targetIndex]] = [row[targetIndex], row[colIndex]]

		newItem.keys = keys
		onUpdate(path, tabIndex, newItem)
	}

	return (
		<div>
			<SchemaPropertySelector
				open={keySelectorOpen}
				onOpenChange={setKeySelectorOpen}
				dataSchema={JSON.stringify(dataSchema)}
				currentScope={item.scope}
				onSelect={handleKeySelect}
			/>

			<div
				draggable
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onClick={(e) => {
					e.stopPropagation()
					onSelect(path, tabIndex)
				}}
				className={`group relative rounded-lg border transition-all cursor-pointer ${isSelected
					? "border-primary bg-primary/5 shadow-sm"
					: "border-border hover:border-primary/50 hover:bg-muted/30"
					} ${isDragging ? "opacity-30" : ""}`}
			>
				<div className="flex items-center gap-3 p-4">
					<div className="cursor-grab active:cursor-grabbing opacity-70 group-hover:opacity-100 transition-opacity">
						<GripVertical className="h-4 w-4 text-muted-foreground" />
					</div>
					<span className="text-muted-foreground">{getIconForType(item.type)}</span>
					<div className="flex-1">
						{isEditingHeader ? (
							<Input
								value={headerValue}
								onChange={(e) => setHeaderValue(e.target.value)}
								onBlur={handleHeaderBlur}
								onKeyDown={handleHeaderKeyDown}
								onClick={(e) => e.stopPropagation()}
								className="h-7 px-2 text-sm font-medium"
								autoFocus
							/>
						) : (
							<div
								className="font-medium text-sm cursor-text hover:bg-muted/50 px-2 py-1 rounded transition-colors"
								onClick={(e) => {
									e.stopPropagation()
									setIsEditingHeader(true)
									setHeaderValue(item.header || "")
								}}
							>
								{item.header || item.type}
							</div>
						)}
						<div className="text-xs text-muted-foreground mt-1">{item.type}</div>
						{item.scope && <div className="text-xs text-muted-foreground/70 mt-0.5">scope: {item.scope}</div>}
						{item.with_key && <div className="text-xs text-muted-foreground/70 mt-0.5">with_key: {item.with_key}</div>}
					</div>
					<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
						<Button
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0"
							onClick={(e) => {
								e.stopPropagation()
								onOpenPropertySelector(path, tabIndex, item.scope)
							}}
						>
							<Settings className="h-4 w-4" />
						</Button>
						<Button
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0 text-destructive hover:text-destructive"
							onClick={(e) => {
								e.stopPropagation()
								onDelete(path, tabIndex)
							}}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				</div>

				{hasKeys && (
					<div className="ml-12 mr-4 mb-3 p-3 bg-muted/30 rounded-md border border-border/50">
						<div className="flex items-center justify-between mb-2">
							<div className="text-xs font-semibold text-muted-foreground">Keys</div>
							<div className="flex items-center gap-2">
								{selectedKeys.length >= 2 && (
									<Button
										variant="default"
										size="sm"
										className="h-6 px-2 text-xs"
										onClick={(e) => {
											e.stopPropagation()
											handleGroupKeys()
										}}
									>
										Group Selected ({selectedKeys.length})
									</Button>
								)}
								<Button
									variant="ghost"
									size="sm"
									className="h-6 px-2 text-xs"
									onClick={(e) => {
										e.stopPropagation()
										handleAddKey()
									}}
								>
									<Plus className="h-3 w-3 mr-1" />
									Select Key
								</Button>
							</div>
						</div>
						<div className="space-y-2">
							{item.keys.map((keyOrRow: string | string[], keyIndex: number) => {
								// Check if this is a row (array) or single key (string)
								const isRow = Array.isArray(keyOrRow)
								const keys = isRow ? keyOrRow : [keyOrRow]
								const isSelected = selectedKeys.includes(keyIndex)

								return (
									<div
										key={keyIndex}
										className={`flex flex-wrap gap-2 ${isRow ? "p-2 bg-muted/50 rounded border border-border/30" : ""} ${isSelected && !isRow ? "ring-2 ring-primary rounded" : ""
											}`}
									>
										{isRow ? (
											<>
												<div className="w-full flex items-center justify-between mb-1">
													<span className="text-[10px] text-muted-foreground font-medium">Row {keyIndex + 1}</span>
													<div className="flex items-center gap-1">
														<Button
															variant="ghost"
															size="sm"
															className="h-4 w-4 p-0"
															onClick={(e) => {
																e.stopPropagation()
																handleMoveKeyUp(keyIndex)
															}}
															disabled={keyIndex === 0}
														>
															<ArrowUp className="h-3 w-3" />
														</Button>
														<Button
															variant="ghost"
															size="sm"
															className="h-4 w-4 p-0"
															onClick={(e) => {
																e.stopPropagation()
																handleMoveKeyDown(keyIndex)
															}}
															disabled={keyIndex === item.keys.length - 1}
														>
															<ArrowDown className="h-3 w-3" />
														</Button>
														<Button
															variant="ghost"
															size="sm"
															className="h-4 px-1 text-[10px]"
															onClick={(e) => {
																e.stopPropagation()
																handleUngroupRow(keyIndex)
															}}
														>
															Ungroup
														</Button>
													</div>
												</div>
												{keys.map((key: string, colIndex: number) => (
													<Badge
														key={colIndex}
														variant="default"
														className="gap-1 pr-1 cursor-grab active:cursor-grabbing"
														draggable
														onDragStart={(e) => handleKeyDragStart(e, keyIndex, colIndex)}
														onDrop={(e) => handleKeyDrop(e, keyIndex, colIndex)}
														onDragOver={handleKeyDragOver}
														style={{
															opacity: draggedKeyPath?.[0] === keyIndex && draggedKeyPath?.[1] === colIndex ? 0.5 : 1,
														}}
													>
														{key}
														<div className="flex items-center gap-0.5 ml-1">
															<Button
																variant="ghost"
																size="sm"
																className="h-3 w-3 p-0 hover:bg-destructive/20"
																onClick={(e) => {
																	e.stopPropagation()
																	handleRemoveKey(keyIndex) // This should remove the key from the row, not the whole row
																}}
															>
																<Trash2 className="h-2.5 w-2.5 text-destructive" />
															</Button>
														</div>
													</Badge>
												))}
											</>
										) : (
											<>
												<div className="flex items-center gap-2 w-full">
													<Checkbox
														checked={isSelected}
														onCheckedChange={() => handleToggleKeySelection(keyIndex)}
														onClick={(e) => e.stopPropagation()}
														className="h-3 w-3"
													/>
													<Badge variant="secondary" className="gap-1 pr-1 flex-1">
														{keyOrRow}
														<div className="flex items-center gap-0.5 ml-auto">
															<Button
																variant="ghost"
																size="sm"
																className="h-4 w-4 p-0"
																onClick={(e) => {
																	e.stopPropagation()
																	handleMoveKeyUp(keyIndex)
																}}
																disabled={keyIndex === 0}
															>
																<ArrowUp className="h-3 w-3" />
															</Button>
															<Button
																variant="ghost"
																size="sm"
																className="h-4 w-4 p-0"
																onClick={(e) => {
																	e.stopPropagation()
																	handleMoveKeyDown(keyIndex)
																}}
																disabled={keyIndex === item.keys.length - 1}
															>
																<ArrowDown className="h-3 w-3" />
															</Button>
															<Button
																variant="ghost"
																size="sm"
																className="h-4 w-4 p-0 hover:bg-destructive/20"
																onClick={(e) => {
																	e.stopPropagation()
																	handleRemoveKey(keyIndex)
																}}
															>
																<Trash2 className="h-3 w-3 text-destructive" />
															</Button>
														</div>
													</Badge>
												</div>
											</>
										)}
									</div>
								)
							})}
						</div>
					</div>
				)}

				{canHaveChildren && (
					<div
						className={`ml-12 mr-4 mb-4 min-h-[100px] border-2 border-dashed rounded-md p-3 transition-all ${isDraggingOver
							? "border-primary bg-primary/10 shadow-inner"
							: "border-border/50 bg-muted/20 hover:border-border hover:bg-muted/30"
							}`}
						onDragLeave={handleDragLeave}
					>
						{children.length === 0 ? (
							<div
								className="flex items-center justify-center h-full text-xs text-muted-foreground py-8"
								onDrop={(e) => handleDrop(e, 0)}
								onDragOver={(e) => handleDragOver(e, 0)}
							>
								<div className="text-center">
									<div className="mb-2 font-medium text-sm">Children</div>
									<div className="mb-1 text-muted-foreground/70">Drop components here</div>
									<div className="text-[10px] opacity-60">Cards, Tables, Lists can be nested</div>
								</div>
							</div>
						) : (
							<div className="space-y-3">
								<div className="text-xs font-semibold text-muted-foreground mb-2 px-1">Children</div>
								{children.map((child: any, childIndex: number) => (
									<div key={childIndex}>
										{dragOverIndex === childIndex && (
											<div className="h-2 bg-primary/20 rounded-full mb-2 border border-dashed border-primary" />
										)}
										<div
											className="h-8 mb-2 relative z-10"
											onDrop={(e) => handleDrop(e, childIndex)}
											onDragOver={(e) => handleDragOver(e, childIndex)}
										/>
										{Array.isArray(child) ? (
											<div className="flex gap-2">
												{child.map((subChild: any, subIndex: number) => (
													<div key={subIndex} className="flex-1">
														<LayoutItemRenderer
															item={subChild}
															path={[...path, childIndex, subIndex]}
															tabIndex={tabIndex}
															selectedPath={selectedPath}
															onSelect={onSelect}
															onDelete={onDelete}
															onDrop={onDrop}
															onUpdate={onUpdate}
															onDragElement={onDragElement}
															onMoveElement={onMoveElement}
															dataSchema={dataSchema}
															onOpenPropertySelector={onOpenPropertySelector}
															onDragEnd={onDragEnd}
														/>
													</div>
												))}
											</div>
										) : (
											<LayoutItemRenderer
												item={child}
												path={[...path, childIndex]}
												tabIndex={tabIndex}
												selectedPath={selectedPath}
												onSelect={onSelect}
												onDelete={onDelete}
												onDrop={onDrop}
												onUpdate={onUpdate}
												onDragElement={onDragElement}
												onMoveElement={onMoveElement}
												dataSchema={dataSchema}
												onOpenPropertySelector={onOpenPropertySelector}
												onDragEnd={onDragEnd}
											/>
										)}
									</div>
								))}
								{dragOverIndex === children.length && (
									<div className="h-2 bg-primary/20 rounded-full mb-2 border border-dashed border-primary" />
								)}
								<div
									className="h-4"
									onDrop={(e) => handleDrop(e, children.length)}
									onDragOver={(e) => handleDragOver(e, children.length)}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export function VisualizerCanvas({
	config,
	dataSchema,
	data,
	document_classifier_id,
	onConfigChange,
	onSchemaChange,
	onElementSelect,
	selectedElement,
}: VisualizerCanvasProps) {
	const [currentTabIndex, setCurrentTabIndex] = useState(0)
	const [propertySelectorOpen, setPropertySelectorOpen] = useState(false)
	const [propertySelectorPath, setPropertySelectorPath] = useState<number[] | null>(null)
	const [propertySelectorTabIndex, setPropertySelectorTabIndex] = useState<number>(0)
	const [propertySelectorCurrentScope, setPropertySelectorCurrentScope] = useState<string | undefined>(undefined)
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
	const [draggingElement, setDraggingElement] = useState<{ path: number[]; tabIndex: number } | null>(null)
	const [viewMode, setViewMode] = useState<"editor" | "preview" | "code">("preview")
	const [codeViewTab, setCodeViewTab] = useState<"config" | "schema">("config")
	const [expandedEditor, setExpandedEditor] = useState<null | "config" | "schema">(null)
	const [configError, setConfigError] = useState<string | null>(null)
	const [schemaError, setSchemaError] = useState<string | null>(null)

	const { document_classifier } = usePage().props
	console.log("data = ", data)
	const tabs = config.tabs || []
	const currentTab = tabs[currentTabIndex]
	const layout = currentTab?.layout || []

	const createNewItem = (type: string): any => {
		const baseItem: any = {
			type,
			header: `${type.charAt(0).toUpperCase() + type.slice(1)} Header`,
		}

		if (type === "card") {
			baseItem.scope = ""
			baseItem.keys = []
			baseItem.children = []
		} else if (type === "card_array") {
			baseItem.scope = ""
			baseItem.index = "item"
			baseItem.keys = []
			baseItem.children = []
		} else if (type === "table" || type === "list") {
			baseItem.scope = ""
			baseItem.key = ""
		}

		return baseItem
	}

	const handleDrop = (componentData: string, targetPath: number[], tabIndex: number, insertIndex?: number) => {
		const component = JSON.parse(componentData)
		const newItem = createNewItem(component.type)

		console.log(
			"[v0] handleDrop - component type:",
			component.type,
			"targetPath:",
			targetPath,
			"tabIndex:",
			tabIndex,
			"insertIndex:",
			insertIndex,
		)

		const newConfig = JSON.parse(JSON.stringify(config))
		const tab = newConfig.tabs[tabIndex]

		if (targetPath.length === 0) {

			if (insertIndex !== undefined) {
				tab.layout.splice(insertIndex, 0, newItem)
			} else {
				tab.layout.push(newItem)
			}
		} else {

			let current = tab.layout
			for (let i = 0; i < targetPath.length; i++) {
				if (i === targetPath.length - 1) {
					const targetItem = current[targetPath[i]]
					if (!targetItem.children) {
						targetItem.children = []
					}
					if (insertIndex !== undefined) {
						targetItem.children.splice(insertIndex, 0, newItem)
					} else {
						targetItem.children.push(newItem)
					}
				} else {
					current = current[targetPath[i]].children || current[targetPath[i]]
				}
			}
		}


		onConfigChange(newConfig)
	}

	const handleRootDrop = (e: React.DragEvent, insertIndex?: number) => {
		e.preventDefault()
		e.stopPropagation()
		setDragOverIndex(null)





		const componentData = e.dataTransfer.getData("component")
		const elementPath = e.dataTransfer.getData("elementPath")

		console.log(
			"[v0] Root drop - componentData:",
			!!componentData,
			"elementPath:",
			!!elementPath,
			"insertIndex:",
			insertIndex,
		)

		if (componentData) {

			handleDrop(componentData, [], currentTabIndex, insertIndex)
		} else if (elementPath) {
			const { path: sourcePath, tabIndex: sourceTabIndex } = JSON.parse(elementPath)
			console.log(
				"[v0] Moving existing element to root - sourcePath:",
				sourcePath,
				"sourceTabIndex:",
				sourceTabIndex,
				"currentTabIndex:",
				currentTabIndex,
			)
			if (sourceTabIndex === currentTabIndex) {
				handleMoveElement(sourcePath, [], currentTabIndex, insertIndex)
			}
		} else {

		}
	}

	const handleDragOver = (e: React.DragEvent, insertIndex?: number) => {
		e.preventDefault()
		e.stopPropagation()
		const hasElementPath = e.dataTransfer.types.includes("elementpath")
		e.dataTransfer.dropEffect = hasElementPath ? "move" : "copy"
		setDragOverIndex(insertIndex ?? layout.length)

	}

	const handleElementSelect = (path: number[], tabIndex: number) => {
		onElementSelect?.({ path, tabIndex })
	}

	const handleElementDelete = (path: number[], tabIndex: number) => {
		const newConfig = JSON.parse(JSON.stringify(config))
		const tab = newConfig.tabs[tabIndex]

		let current = tab.layout
		for (let i = 0; i < path.length - 1; i++) {
			current = current[path[i]].children || current[path[i]]
		}
		current.splice(path[path.length - 1], 1)

		onConfigChange(newConfig)
	}

	const handleElementUpdate = (path: number[], tabIndex: number, updatedItem: any) => {
		const newConfig = JSON.parse(JSON.stringify(config))
		const tab = newConfig.tabs[tabIndex]

		let current = tab.layout
		for (let i = 0; i < path.length - 1; i++) {
			current = current[path[i]].children || current[path[i]]
		}
		current[path[path.length - 1]] = updatedItem

		onConfigChange(newConfig)
	}

	const handleOpenPropertySelector = (path: number[], tabIndex: number, currentScope?: string) => {
		setPropertySelectorPath(path)
		setPropertySelectorTabIndex(tabIndex)
		setPropertySelectorCurrentScope(currentScope)
		setPropertySelectorOpen(true)
	}

	const handlePropertySelect = (scope: string) => {
		if (propertySelectorPath === null) return

		const newConfig = JSON.parse(JSON.stringify(config))
		const tab = newConfig.tabs[propertySelectorTabIndex]

		let current = tab.layout
		for (let i = 0; i < propertySelectorPath.length - 1; i++) {
			current = current[propertySelectorPath[i]].children || current[propertySelectorPath[i]]
		}

		const item = current[propertySelectorPath[propertySelectorPath.length - 1]]
		item.scope = scope

		onConfigChange(newConfig)
	}

	const handleAddTab = () => {
		const newConfig = JSON.parse(JSON.stringify(config))
		newConfig.tabs.push({
			title: `Tab ${tabs.length + 1}`,
			layout: [],
		})
		onConfigChange(newConfig)
		setCurrentTabIndex(tabs.length)
	}

	const handleTabTitleChange = (index: number, newTitle: string) => {
		const newConfig = JSON.parse(JSON.stringify(config))
		newConfig.tabs[index].title = newTitle
		onConfigChange(newConfig)
	}

	const handleMoveElement = (sourcePath: number[], targetPath: number[], tabIndex: number, insertIndex?: number) => {
		console.log(
			"[v0] handleMoveElement - sourcePath:",
			sourcePath,
			"targetPath:",
			targetPath,
			"tabIndex:",
			tabIndex,
			"insertIndex:",
			insertIndex,
		)

		if (sourcePath.length === 1 && targetPath.length === 0 && insertIndex !== undefined) {

			const fromIndex = sourcePath[0]
			const toIndex = insertIndex

			const newConfig = JSON.parse(JSON.stringify(config))
			const tab = newConfig.tabs[tabIndex]


			console.log(
				"[v0] Layout before:",
				tab.layout.map((item: any) => item.header || item.type),
			)

			const [movedItem] = tab.layout.splice(fromIndex, 1)

			const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex

			tab.layout.splice(adjustedToIndex, 0, movedItem)

			console.log(
				"[v0] Layout after:",
				tab.layout.map((item: any) => item.header || item.type),
			)




			onConfigChange(newConfig)

			return
		}

		const newConfig = JSON.parse(JSON.stringify(config))
		const tab = newConfig.tabs[tabIndex]

		const getElementAtPath = (layout: any[], path: number[]) => {
			let current = layout
			for (let i = 0; i < path.length - 1; i++) {
				const item = current[path[i]]
				current = Array.isArray(item) ? item : item.children || []
			}
			return { parent: current, index: path[path.length - 1] }
		}

		const { parent: sourceParent, index: sourceIndex } = getElementAtPath(tab.layout, sourcePath)

		const [movedElement] = sourceParent.splice(sourceIndex, 1)


		let adjustedInsertIndex = insertIndex
		if (sourcePath.length === targetPath.length) {
			const sameParent = sourcePath.slice(0, -1).every((val, idx) => val === targetPath[idx])
			if (sameParent && sourceIndex < (insertIndex ?? 0)) {
				adjustedInsertIndex = (insertIndex ?? 0) - 1

			}
		}

		if (targetPath.length === 0) {

			tab.layout.splice(adjustedInsertIndex ?? tab.layout.length, 0, movedElement)
		} else {

			const { parent: targetParent } = getElementAtPath(tab.layout, targetPath)
			const targetItem = targetParent[targetPath[targetPath.length - 1]]

			if (!targetItem.children) {
				targetItem.children = []
			}
			targetItem.children.splice(adjustedInsertIndex ?? targetItem.children.length, 0, movedElement)
		}


		onConfigChange(newConfig)
	}

	const handleDragEnd = () => {


		if (draggingElement && dragOverIndex !== null) {

			handleMoveElement(draggingElement.path, [], draggingElement.tabIndex, dragOverIndex)
		}

		setDraggingElement(null)
		setDragOverIndex(null)
	}

	const handleCanvasDrop = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()




		const componentData = e.dataTransfer.getData("component")
		const elementPath = e.dataTransfer.getData("elementPath")



		if (componentData && !elementPath) {
			const insertIndex = dragOverIndex !== null ? dragOverIndex : layout.length

			handleDrop(componentData, [], currentTabIndex, insertIndex)
			setDragOverIndex(null) // Reset drag over index after drop
		}
	}

	const handleCanvasDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		const hasComponent = e.dataTransfer.types.includes("component")
		if (hasComponent) {
			e.dataTransfer.dropEffect = "copy"
		}
	}

	const handleSave = () => {

		console.log("extraction_schema = ", dataSchema)

		fetch(`/document_classifier/${document_classifier_id}`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-Token': getCSRFToken() || ''
			},
			body: JSON.stringify(
				{
					extraction_schema: dataSchema,
					visualizer_json: config
				}
			)
		})


	}

	const handleExport = () => {
		const dataStr = JSON.stringify(config, null, 2)
		const dataBlob = new Blob([dataStr], { type: "application/json" })
		const url = URL.createObjectURL(dataBlob)
		const link = document.createElement("a")
		link.href = url
		link.download = "visualizer-config.json"
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)

	}

	const handleConfigChange = (newValue: string) => {
		try {
			const parsedConfig = JSON.parse(newValue)
			setConfigError(null)
			onConfigChange(parsedConfig)

		} catch (error) {
			setConfigError(error instanceof Error ? error.message : "Invalid JSON")

		}
	}

	const handleSchemaChange = (newValue: string) => {
		try {
			const parsedSchema = JSON.parse(newValue)
			setSchemaError(null)
			// Update the schema in the visualizer context
			//const event = new CustomEvent("schema-update", { detail: parsedSchema })
			//window.dispatchEvent(event)
			console.log("parsedSchema = ", parsedSchema)
			onSchemaChange(parsedSchema)

		} catch (error) {
			setSchemaError(error instanceof Error ? error.message : "Invalid JSON")

		}
	}

	return (
		<>
			<SchemaPropertySelector
				open={propertySelectorOpen}
				onOpenChange={setPropertySelectorOpen}
				dataSchema={JSON.stringify(dataSchema)}
				currentScope={propertySelectorCurrentScope}
				onSelect={handlePropertySelect}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<div className="border-b border-border px-4 py-2 bg-muted/30 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Button
							variant={viewMode === "editor" ? "default" : "ghost"}
							size="sm"
							onClick={() => setViewMode("editor")}
							className="h-8 gap-2"
						>
							<Layout className="h-4 w-4" />
							<span className="text-sm">Editor</span>
						</Button>
						<Button
							variant={viewMode === "preview" ? "default" : "ghost"}
							size="sm"
							onClick={() => setViewMode("preview")}
							className="h-8 gap-2"
						>
							<Eye className="h-4 w-4" />
							<span className="text-sm">Preview</span>
						</Button>
						<Button
							variant={viewMode === "code" ? "default" : "ghost"}
							size="sm"
							onClick={() => setViewMode("code")}
							className="h-8 gap-2"
						>
							<Code className="h-4 w-4" />
							<span className="text-sm">Code</span>
						</Button>
					</div>
					<div className="flex items-center gap-2">

						<Button variant="outline" size="sm"
							onClick={handleSave}
							disabled={JSON.stringify(config) === JSON.stringify(document_classifier?.visualizer_json)}
							className={`h-8 gap-2 ${JSON.stringify(config) === JSON.stringify(document_classifier?.visualizer_json) ? "" : "bg-red-500"} border-none hover:bg-red-600`}>
							<Save className="h-4 w-4" />
							<span className="text-sm">Save</span>
						</Button>
						<Button variant="outline" size="sm" onClick={handleExport} className="h-8 gap-2 bg-transparent">
							<Download className="h-4 w-4" />
							<span className="text-sm">Export</span>
						</Button>
					</div>
				</div>

				{viewMode === "code" ? (
					<div className="flex-1 p-8 bg-muted/20 overflow-auto">
						{/* Adjusted max-width for wider view */}
						<div className="max-w-7xl mx-auto h-full flex flex-row gap-6">
							{(expandedEditor === null || expandedEditor === "config") && (
								<div className="bg-background rounded-lg border border-border shadow-sm flex-1 flex flex-col overflow-hidden min-h-[400px]">
									<div className="flex items-center justify-between p-4 border-b border-border">
										<div className="flex items-center gap-2">
											<h3 className="text-lg font-semibold">Configuration JSON</h3>
											{configError && (
												<span className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">
													{configError}
												</span>
											)}
										</div>
										<div className="flex items-center gap-2">
											<Button
												variant="outline"
												size="sm"
												onClick={() => {
													navigator.clipboard.writeText(JSON.stringify(config, null, 2))

												}}
												className="h-8 gap-2"
											>
												<span className="text-sm">Copy</span>
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => setExpandedEditor(expandedEditor === "config" ? null : "config")}
												className="h-8 w-8 p-0"
											>
												{expandedEditor === "config" ? (
													<Minimize2 className="h-4 w-4" />
												) : (
													<Maximize2 className="h-4 w-4" />
												)}
											</Button>
										</div>
									</div>
									<div className="flex-1 overflow-hidden">
										<MonacoEditor
											value={JSON.stringify(config, null, 2)}
											onChange={handleConfigChange}
											language="json"
										/>
									</div>
								</div>
							)}

							{(expandedEditor === null || expandedEditor === "schema") && (
								<div className="bg-background rounded-lg border border-border shadow-sm flex-1 flex flex-col overflow-hidden min-h-[400px]">
									<div className="flex items-center justify-between p-4 border-b border-border">
										<div className="flex items-center gap-2">
											<h3 className="text-lg font-semibold">Data Schema</h3>
											{schemaError && (
												<span className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">
													{schemaError}
												</span>
											)}
										</div>
										<div className="flex items-center gap-2">
											<Button
												variant="outline"
												size="sm"
												onClick={() => {
													navigator.clipboard.writeText(JSON.stringify(dataSchema, null, 2))

												}}
												className="h-8 gap-2"
											>
												<span className="text-sm">Copy</span>
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => setExpandedEditor(expandedEditor === "schema" ? null : "schema")}
												className="h-8 w-8 p-0"
											>
												{expandedEditor === "schema" ? (
													<Minimize2 className="h-4 w-4" />
												) : (
													<Maximize2 className="h-4 w-4" />
												)}
											</Button>
										</div>
									</div>
									<div className="flex-1 overflow-hidden">
										<MonacoEditor
											value={JSON.stringify(dataSchema, null, 2)}
											onChange={handleSchemaChange}
											language="json"
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				) : viewMode === "preview" ? (
					<FormPreview config={config} dataSchema={dataSchema} data={data} />
				) : (
					<div className="flex-1 p-8 bg-muted/20 overflow-auto">
						<div className="max-w-4xl mx-auto">
							<div className="bg-background rounded-lg border border-border shadow-sm min-h-[600px]">
								<Tabs value={currentTabIndex.toString()} onValueChange={(v) => setCurrentTabIndex(Number.parseInt(v))}>
									<div className="border-b border-border px-6 pt-4 flex items-center justify-between">
										<TabsList>
											{tabs.map((tab: any, index: number) => (
												<TabsTrigger key={index} value={index.toString()}>
													<Input
														value={tab.title}
														onChange={(e) => {
															e.stopPropagation()
															handleTabTitleChange(index, e.target.value)
														}}
														onClick={(e) => e.stopPropagation()}
														className="h-6 px-2 text-sm border-0 bg-transparent focus-visible:ring-0"
													/>
												</TabsTrigger>
											))}
										</TabsList>
										<Button variant="ghost" size="sm" onClick={handleAddTab} className="gap-2">
											<Plus className="h-4 w-4" />
											Add Tab
										</Button>
									</div>

									{tabs.map((tab: any, tabIndex: number) => (
										<TabsContent
											key={tabIndex}
											value={tabIndex.toString()}
											className="p-6 mt-0"
											onDrop={handleCanvasDrop}
											onDragOver={handleCanvasDragOver}
										>
											{layout.length === 0 ? (
												<div
													className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed rounded-lg"
													onDrop={(e) => handleRootDrop(e, 0)}
													onDragOver={(e) => handleDragOver(e, 0)}
												>
													<div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
														<svg
															className="w-8 h-8 text-muted-foreground"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
														</svg>
													</div>
													<p className="text-sm text-muted-foreground">Drag components from the left panel to start</p>
												</div>
											) : (
												<div className="space-y-3">
													{layout.map((item: any, index: number) => (
														<div key={index}>
															{dragOverIndex === index && (
																<div className="h-3 bg-primary/20 rounded-full mb-2 border border-dashed border-primary" />
															)}
															<div
																className="h-8 mb-2 relative z-10"
																onDrop={(e) => {

																	handleRootDrop(e, index)
																}}
																onDragOver={(e) => {

																	handleDragOver(e, index)
																}}
															/>
															{Array.isArray(item) ? (
																<div className="flex gap-2">
																	{item.map((subItem: any, subIndex: number) => (
																		<div key={subIndex} className="flex-1">
																			<LayoutItemRenderer
																				item={subItem}
																				path={[index, subIndex]}
																				tabIndex={tabIndex}
																				selectedPath={selectedElement?.path || null}
																				onSelect={handleElementSelect}
																				onDelete={handleElementDelete}
																				onDrop={handleDrop}
																				onUpdate={handleElementUpdate}
																				onDragElement={(path, tabIdx) => {

																					setDraggingElement({ path, tabIndex: tabIdx })
																				}}
																				onMoveElement={handleMoveElement}
																				dataSchema={dataSchema}
																				onOpenPropertySelector={handleOpenPropertySelector}
																				onDragEnd={handleDragEnd}
																			/>
																		</div>
																	))}
																</div>
															) : (
																<LayoutItemRenderer
																	item={item}
																	path={[index]}
																	tabIndex={tabIndex}
																	selectedPath={selectedElement?.path || null}
																	onSelect={handleElementSelect}
																	onDelete={handleElementDelete}
																	onDrop={handleDrop}
																	onUpdate={handleElementUpdate}
																	onDragElement={(path, tabIdx) => {

																		setDraggingElement({ path, tabIndex: tabIdx })
																	}}
																	onMoveElement={handleMoveElement}
																	dataSchema={dataSchema}
																	onOpenPropertySelector={handleOpenPropertySelector}
																	onDragEnd={handleDragEnd}
																/>
															)}
														</div>
													))}
													{dragOverIndex === layout.length && (
														<div className="h-3 bg-primary/20 rounded-full border border-dashed border-primary" />
													)}
													<div
														className="h-16 mt-4 relative z-10"
														onDrop={(e) => {

															handleRootDrop(e, layout.length)
														}}
														onDragOver={(e) => {

															handleDragOver(e, layout.length)
														}}
														onDragEnd={handleDragEnd}
													/>
												</div>
											)}
										</TabsContent>
									))}
								</Tabs>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	)
}
